import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useMemberGroupData } from 'hooks/useMemberGroupData';

import { Database } from 'libs/supabaseTypes';
import { formatDate, getRelationshipToToday } from 'libs/utils';

import { InfoCard } from 'components/ui/InfoCard';

import { Bumper, GridListWrapper } from 'styles/shared';
import { Fonts, spacing } from 'styles/theme';

export type RetroData = Database['public']['Tables']['retros']['Row'];

export const RetroList = () => {
    const { retros } = useMemberGroupData();

    const [, setSearchParams] = useSearchParams();

    const hasCompletedRetroToday = useMemo(() => {
        let isRetroDateToday = false;
        retros.forEach((retro) => {
            if (
                getRelationshipToToday(new Date(retro.created_at ?? 0)) ===
                'current'
            ) {
                isRetroDateToday = true;
            }
        });
        return isRetroDateToday;
    }, [retros]);

    const onSelectNewRetro = useCallback(() => {
        setSearchParams({
            modalContent: 'retro',
        });
    }, [setSearchParams]);

    const onSelectPreviousRetro = useCallback(
        (retroId: number) => () => {
            setSearchParams({
                modalContent: 'retro-results',
                retroId: retroId.toString(),
            });
        },
        [setSearchParams]
    );

    return (
        <>
            <Bumper $minHeight={spacing.small} />
            <Fonts.MediumQuote>
                {`This is your place to track personal growth and stay accountable to your goals. Reflecting allows you to see your progress.`}
            </Fonts.MediumQuote>
            <Bumper />
            <GridListWrapper $gap={spacing.large}>
                {hasCompletedRetroToday ? (
                    <InfoCard
                        dateRelationship="current"
                        headerText={formatDate(new Date())}
                        mainText="Retake Today's Grand Retro"
                        onClick={onSelectNewRetro}
                    />
                ) : (
                    <InfoCard
                        dateRelationship="current"
                        headerText={formatDate(new Date())}
                        mainText="Start New Grand Retro"
                        onClick={onSelectNewRetro}
                    />
                )}
                {retros.map((retro) => (
                    <InfoCard
                        key={retro.period}
                        dateRelationship="previous"
                        headerText={formatDate(new Date(retro.created_at ?? 0))}
                        mainText="Grand Retro Results"
                        onClick={onSelectPreviousRetro(retro.id)}
                    />
                ))}
            </GridListWrapper>
            <Bumper $minHeight={spacing.xxxSmall} />
        </>
    );
};
